import { useCallback, useEffect, useRef } from "react";

import { components } from "@/@types/api";
import { useAnalytics } from "@/hooks/useAnalytics";

import { ScenarioType, ScenarioTypeEnum } from "../../types";

type Scenario = components["schemas"]["NewScenarioV2"];

interface UseScenarioAnalyticsV3Props {
  scenario?: Scenario;
}

interface ScenarioCreatedOptions {
  isCloned?: boolean;
  isQuickScenario?: boolean;
}

export const useScenarioAnalyticsV3 = ({ scenario }: UseScenarioAnalyticsV3Props) => {
  const {
    scenarioCreated,
    scenarioUpdated,
    scenarioSubmitted,
    scenarioFormError,
    scenarioReportViewedSection,
    scenarioSpendInputsPageViewed,
    scenarioBreakdownTableDownloaded,
  } = useAnalytics();

  const trackScenarioCreated = useCallback(
    (options: ScenarioCreatedOptions = {}) => {
      const { isCloned = false, isQuickScenario = false } = options;

      if (!scenario?.uuid || !scenario?.name || !scenario?.scenarioType) return;

      scenarioCreated({
        scenarioType: ScenarioType.toString(scenario.scenarioType as ScenarioTypeEnum),
        scenarioId: scenario.uuid,
        scenarioName: scenario.name,
        isCloned: isCloned,
        isQuickScenario: isQuickScenario,
        version: 3,
      });
    },
    [scenario, scenarioCreated]
  );

  const trackScenarioUpdated = useCallback(() => {
    if (!scenario?.uuid || !scenario?.name || !scenario?.scenarioType) return;

    scenarioUpdated({
      scenarioType: ScenarioType.toString(scenario.scenarioType as ScenarioTypeEnum),
      scenarioId: scenario.uuid,
      scenarioName: scenario.name,
      version: 3,
    });
  }, [scenario, scenarioUpdated]);

  const trackScenarioSubmitted = useCallback(
    (analyticsData?: {
      budgetRatio?: number;
      scenarioLengthMonths?: number;
      mediaSpendInputs?: number;
      mediaSpendConstraints?: number;
      isQuickScenario?: boolean;
    }) => {
      if (!scenario?.uuid || !scenario?.name || !scenario?.scenarioType) return;

      scenarioSubmitted({
        scenarioName: scenario.name,
        scenarioId: scenario.uuid,
        scenarioType: ScenarioType.toString(scenario.scenarioType as ScenarioTypeEnum),
        budgetRatio: analyticsData?.budgetRatio,
        scenarioLengthMonths: analyticsData?.scenarioLengthMonths,
        mediaSpendInputs: analyticsData?.mediaSpendInputs,
        mediaSpendConstraints: analyticsData?.mediaSpendConstraints,
        isQuickScenario: analyticsData?.isQuickScenario,
        version: 3,
      });
    },
    [scenario, scenarioSubmitted]
  );

  const trackScenarioFormError = useCallback(
    (errorMessage: string) => {
      if (!scenario?.uuid || !scenario?.name || !scenario?.scenarioType) return;

      scenarioFormError({
        scenarioId: scenario.uuid,
        scenarioName: scenario.name,
        scenarioType: ScenarioType.toString(scenario.scenarioType as ScenarioTypeEnum),
        scenarioError: errorMessage,
        version: 3,
      });
    },
    [scenario, scenarioFormError]
  );

  const trackReportPageDuration = useCallback(
    (pageName: string, durationMS: number) => {
      if (!scenario?.uuid || !scenario?.name || !scenario?.scenarioType) {
        return;
      }

      const scenarioType = ScenarioType.toString(scenario.scenarioType as ScenarioTypeEnum);
      const scenarioId = scenario.uuid;
      const scenarioName = scenario.name;
      const section = pageName;
      const duration = durationMS;
      const version = 3;

      scenarioReportViewedSection(
        scenarioType,
        scenarioId,
        scenarioName,
        section,
        duration,
        version
      );
    },
    [scenario, scenarioReportViewedSection]
  );

  const trackSpendInputsPageDuration = useCallback(
    (durationMS: number) => {
      if (!scenario?.uuid || !scenario?.name || !scenario?.scenarioType) {
        return;
      }

      const scenarioType = ScenarioType.toString(scenario.scenarioType as ScenarioTypeEnum);
      const scenarioId = scenario.uuid;
      const scenarioName = scenario.name;
      const duration = durationMS;
      const version = 3;

      scenarioSpendInputsPageViewed(scenarioType, scenarioId, scenarioName, duration, version);
    },
    [scenario, scenarioSpendInputsPageViewed]
  );

  const trackScenarioBreakdownTableDownloaded = useCallback(() => {
    if (!scenario?.uuid || !scenario?.name || !scenario?.scenarioType) {
      return;
    }

    const scenarioType = ScenarioType.toString(scenario.scenarioType as ScenarioTypeEnum);
    const scenarioId = scenario.uuid;
    const scenarioName = scenario.name;
    const version = 3;

    scenarioBreakdownTableDownloaded(scenarioType, scenarioId, scenarioName, version);
  }, [scenario, scenarioBreakdownTableDownloaded]);

  return {
    trackScenarioCreated,
    trackScenarioUpdated,
    trackScenarioSubmitted,
    trackScenarioFormError,
    trackReportPageDuration,
    trackSpendInputsPageDuration,
    trackScenarioBreakdownTableDownloaded,
  };
};

interface DurationTrackingOptions {
  trackingInterval?: number; // milliseconds, default 30000 (30 seconds)
  minimumDuration?: number; // milliseconds, default 3000 (3 seconds)
}

// Hook for tracking how long users view specific report sections
export const useReportSectionTracking = (
  trackFunction: (sectionName: string, durationMS: number) => void,
  sectionName: string,
  options: DurationTrackingOptions = {}
) => {
  const {
    trackingInterval = 30_000, // 30 seconds
    minimumDuration = 3_000, // 3 seconds
  } = options;

  return useDurationTracking(
    (duration) => trackFunction(sectionName, duration),
    trackingInterval,
    minimumDuration
  );
};

// Hook for tracking how long users spend on a page
export const usePageVisitTracking = (
  trackFunction: (durationMS: number) => void,
  options: DurationTrackingOptions = {}
) => {
  const {
    trackingInterval = 30_000, // 30 seconds
    minimumDuration = 3_000, // 3 seconds
  } = options;

  return useDurationTracking(trackFunction, trackingInterval, minimumDuration);
};

// Core duration tracking implementation
const useDurationTracking = (
  trackFunction: (durationMS: number) => void,
  trackingInterval: number,
  minimumDuration: number
) => {
  const sessionStartRef = useRef<number>(Date.now());
  const accumulatedTimeRef = useRef<number>(0);
  const lastEventTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const trackFunctionRef = useRef(trackFunction);

  // Keep tracking function current
  trackFunctionRef.current = trackFunction;

  useEffect(() => {
    const sessionStart = Date.now();
    sessionStartRef.current = sessionStart;
    accumulatedTimeRef.current = 0;
    lastEventTimeRef.current = 0;

    const handleVisibilityChange = () => {
      const now = Date.now();

      if (document.hidden) {
        // Tab became hidden - accumulate the active time
        accumulatedTimeRef.current += now - sessionStartRef.current;
      } else {
        // Tab became visible - reset session start
        sessionStartRef.current = now;
      }
    };

    // Single interval handles both periodic events and time tracking
    intervalRef.current = setInterval(() => {
      if (document.hidden) return; // Skip when tab is hidden

      const currentActiveTime = Date.now() - sessionStartRef.current;
      const totalActiveTime = accumulatedTimeRef.current + currentActiveTime;

      // Emit periodic events every 30 seconds
      if (
        totalActiveTime >= trackingInterval &&
        totalActiveTime - lastEventTimeRef.current >= trackingInterval
      ) {
        lastEventTimeRef.current = totalActiveTime;
        trackFunctionRef.current(totalActiveTime);
      }
    }, 1000);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Calculate final duration (only if tab is currently visible)
      const finalDuration =
        accumulatedTimeRef.current + (document.hidden ? 0 : Date.now() - sessionStartRef.current);

      if (finalDuration >= minimumDuration) {
        trackFunctionRef.current(finalDuration);
      }
    };
  }, [trackingInterval, minimumDuration]);

  return {
    getCurrentDuration: () => {
      const currentActiveTime = document.hidden ? 0 : Date.now() - sessionStartRef.current;
      return accumulatedTimeRef.current + currentActiveTime;
    },
  };
};
